import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useMutation } from '@tanstack/react-query';
import * as Avatar from '@givebutter/primitives/avatar';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import * as Popover from '@givebutter/primitives/popover';
import { Separator } from '@givebutter/primitives/separator';
import { Tag } from '@givebutter/primitives/tag';
import { useToast } from '@givebutter/primitives/toast';
import { faHashtag, faPlus, faTag } from '@fortawesome/pro-regular-svg-icons';
import { faTags } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { updateContact } from 'api/contacts/single';
import { useGivebutterFormContext } from 'components/form';
import * as Form from 'components/form';
import * as Select from 'components/select';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useSuspenseCurrentAccountTags } from 'customHooks/useCurrentAccountTags';
import { useCurrentContact } from 'customHooks/useCurrentContact';
import { sortBy } from 'lodash';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import styled from 'styled-components';
import { twJoin } from 'tailwind-merge';
import { Contact } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface ContactTagsFormValues {
    tags: string[];
}

const ContactSidebarTags = () => {
    const { error } = useToast();
    const { accountId } = useCurrentAccount();
    const { data: contact } = useCurrentContact();
    const { data: accountContactTags } = useSuspenseCurrentAccountTags();
    const persistedTags = useMemo(() => contact.tags ?? [], [contact.tags]);

    const defaultValues = useMemo(
        () => ({
            tags: accountContactTags
                .filter(tag => persistedTags.indexOf(tag.name) > -1)
                .map(tag => tag.name),
        }),
        [accountContactTags, persistedTags],
    );
    const methods = Form.useGivebutterForm<ContactTagsFormValues>({ defaultValues });
    const { formState, handleSubmit, watch, reset } = methods;
    const { isDirty } = formState;
    const formTags = watch('tags');
    const displayedTags = sortBy(formTags, t => t.toLowerCase());

    const { mutate: submit, isPending: isLoading } = useMutation<
        Contact,
        GivebutterAxiosError,
        ContactTagsFormValues
    >({
        mutationFn: values => {
            return updateContact(accountId, contact.id, {
                tags: values.tags.map(tag => tag.trim()),
            }).then(response => response.data);
        },
        onSuccess: ({ tags }) => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.contacts(accountId),
            });
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.tags(accountId),
                type: 'all',
            });
            reset({ tags });
        },
        onError: e => {
            error(e.response?.data.message || 'Unable to update tags.');
            reset(defaultValues);
        },
    });

    const onFormSubmit = (data: ContactTagsFormValues) => {
        if (!isDirty) return;
        submit(data);
    };

    return (
        <Box className={twJoin(isLoading && 'is-loading')}>
            <div className="px-4 py-3">
                <div className="flex items-center justify-between">
                    <p className="text-base font-medium text-gray-900">Tags</p>
                    <Popover.Root onOpenChange={isOpen => !isOpen && handleSubmit(onFormSubmit)()}>
                        <Popover.Trigger asChild>
                            <Button variant="none" color="primary" size="sm">
                                {displayedTags.length > 0 ? (
                                    <>
                                        <FontAwesomeIcon icon={faTag} />
                                        Edit tags
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faPlus} />
                                        Add tag
                                    </>
                                )}
                            </Button>
                        </Popover.Trigger>
                        <Popover.Portal>
                            <Popover.Content
                                align="end"
                                side="bottom"
                                sideOffset={8}
                                className="max-w-lg px-3.5 py-2.5"
                            >
                                <Form.Provider {...methods}>
                                    <form
                                        onSubmit={handleSubmit(onFormSubmit)}
                                        className="flex flex-col gap-2"
                                    >
                                        <ContactTagsSearchableInput disabled={isLoading} />
                                    </form>
                                </Form.Provider>
                            </Popover.Content>
                        </Popover.Portal>
                    </Popover.Root>
                </div>
            </div>
            <Separator />
            {displayedTags.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-2 p-4">
                    <Avatar.Root size="xs">
                        <Avatar.Fallback aria-label="No tags icon">
                            <FontAwesomeIcon className="text-gray-600" icon={faTags} />
                        </Avatar.Fallback>
                    </Avatar.Root>
                    <p className="text-center text-sm text-gray-500">
                        Add tags to this contact for improved organization.
                    </p>
                </div>
            ) : (
                <div className="flex flex-wrap gap-2 p-3">
                    {displayedTags.map(tag => (
                        <Tag key={tag}>
                            <FontAwesomeIcon icon={faHashtag} size="xs" className="text-gray-400" />
                            {tag}
                        </Tag>
                    ))}
                </div>
            )}
        </Box>
    );
};

const tempCreateTag = 'TEMP_TAG';

interface ContactTagsSearchableInputProps {
    disabled: boolean;
}

const ContactTagsSearchableInput = ({ disabled = false }: ContactTagsSearchableInputProps) => {
    const { data: accountContactTags } = useSuspenseCurrentAccountTags();
    const [inputValue, setInputValue] = useState('');
    const [localTags, setLocalTags] = useState<string[]>([]);
    const formContext = useGivebutterFormContext<ContactTagsFormValues>();
    const { control, setValue, watch } = formContext;
    const tagsValue = watch('tags');
    const createItemRef = useRef<HTMLLIElement | null>(null);
    const closeButtonRef = useRef<HTMLButtonElement | null>(null);

    const tags = sortBy([...localTags, ...accountContactTags.map(tag => tag.name)], t =>
        t.toLowerCase(),
    );
    const showNewTagListItem =
        inputValue.length > 0 &&
        tags.findIndex(tag => tag.toLowerCase().trim() === inputValue.toLowerCase().trim()) === -1;

    useEffect(() => {
        setLocalTags([]);
    }, [accountContactTags]);

    const onSelectItem = useCallback(
        (selectedItem: string | null, selectedItems: string[]) => {
            if (selectedItem && selectedItem === tempCreateTag) {
                if (!selectedItems.find(item => item.trim() === inputValue.trim())) {
                    flushSync(() => {
                        setLocalTags(tags => [...tags, inputValue]);
                        setValue('tags', [...tagsValue, inputValue]);
                    });

                    closeButtonRef?.current?.click();
                }
                setInputValue('');
            }
        },
        [inputValue, tagsValue, setValue],
    );

    return (
        <StyledTagListItemsWrapper>
            <Popover.Close className="hidden" ref={closeButtonRef} />
            <Select.UseFormSearchableSelect
                control={control}
                name="tags"
                headless={true}
                showTags={false}
                multi={true}
                disabled={disabled}
                flat={true}
                alwaysVisibleItemIds={[tempCreateTag]}
                onSelectItem={onSelectItem}
                inputProps={{
                    autoFocus: true,
                    value: inputValue,
                    placeholder: 'Search tags',
                    onChange: e => setInputValue(e.target.value),
                    onKeyDown: e => {
                        const value = e.currentTarget.value;
                        if (e.key === 'Enter' && value.length > 0) {
                            e.preventDefault();
                            createItemRef.current?.click();
                        }
                    },
                }}
            >
                <div className="flex flex-col gap-2">
                    <Select.SearchableInput />
                    <div className="max-h-44 overflow-y-auto">
                        {showNewTagListItem && (
                            <Select.ListItem value={tempCreateTag} ref={createItemRef}>
                                <span className="font-medium text-blue-500">Create</span>
                                <div className="flex items-center gap-1">
                                    <FontAwesomeIcon
                                        icon={faHashtag}
                                        className="text-gray-400"
                                        size="xs"
                                    />
                                    <span className="text-gray-900">{inputValue}</span>
                                </div>
                            </Select.ListItem>
                        )}
                        {tags.map(tag => (
                            <Select.ListItem key={tag} value={tag} checkable>
                                <div className="flex items-center gap-1">
                                    <FontAwesomeIcon
                                        icon={faHashtag}
                                        size="xs"
                                        className="text-gray-400"
                                    />{' '}
                                    {tag}
                                </div>
                            </Select.ListItem>
                        ))}
                    </div>
                </div>
            </Select.UseFormSearchableSelect>
        </StyledTagListItemsWrapper>
    );
};

const StyledTagListItemsWrapper = styled.div`
    ${Select.SelectListInnerContainer} {
        max-height: initial;
        overflow-y: initial;
    }
`;

export { ContactSidebarTags };
